// FOND D'EAU (21/07, demande Claude-Alain : « les pièces d'or, les échelles et les téléporteurs
// ne doivent pas effacer l'eau ») : la grille n'a qu'UNE tuile par case, donc poser une pièce
// (case vide + entité), une échelle ou un téléporteur dans un bassin remplace la tuile eau et
// « troue » la nappe au rendu. Ce module PUR (zéro DOM -> testé par tests/water-bg) calcule le
// masque de la NAPPE COMPLÈTE : eau réelle + cases non-solides à peindre en eau de fond.
//
// Règle (point fixe, monotone) : une case NON-SOLIDE éligible devient « nappe » si elle est
// FERMÉE des deux côtés — eau/nappe, solide ou bord de carte — avec de la nappe sur au moins un
// côté ou au-dessus. Couvre : la pièce au milieu du bassin, le coin de bassin (mur + eau), la
// colonne d'échelle contre le bord de carte, le puits confiné sous la nappe. Ne couvre PAS
// (voulu) : la berge (air de l'autre côté) et l'échelle qui ressort du bassin dans une salle
// d'air. La lave, le feu et la zone mortelle ne sont jamais noyés. L'eau ne changeant JAMAIS en
// cours de partie, le masque se calcule une fois par niveau (cache côté render).
import { isSolid } from '../shared/physics'
import { T } from '../shared/types'

const eligible = (t: T): boolean =>
  !isSolid(t) && t !== T.Water && t !== T.Lava && t !== T.Fire && t !== T.DeathZone

/** Masque 0/1 de la nappe (eau réelle incluse), dimensions cols×rows. */
export function computeWaterBg(terrain: Uint8Array | readonly number[], cols: number, rows: number): Uint8Array {
  const n = cols * rows
  const wet = new Uint8Array(n)
  for (let i = 0; i < n; i++) if (terrain[i] === T.Water) wet[i] = 1
  let changed = true
  while (changed) {
    changed = false
    for (let row = 0; row < rows; row++) {
      let col = 0
      while (col < cols) {
        const i = row * cols + col
        if (wet[i] || !eligible((terrain[i] ?? 0) as T)) { col++; continue }
        // SEGMENT : la suite maximale de cases franchissables encore sèches. C'est
        // lui l'unité, pas la case. Deux cases voisines examinées séparément se
        // bloquaient l'une l'autre — chacune attendait que l'autre soit mouillée
        // pour se déclarer fermée — et le point fixe s'arrêtait sur un rectangle
        // sec au milieu du bassin (29/07, vu à l'image : une corde de deux cases).
        let fin = col
        while (fin + 1 < cols && !wet[row * cols + fin + 1]
          && eligible((terrain[row * cols + fin + 1] ?? 0) as T)) fin++
        const long = fin - col + 1
        const lWet = col > 0 && wet[i - 1] === 1
        const rWet = fin < cols - 1 && wet[row * cols + fin + 1] === 1
        // Le BORD DE CARTE ne ferme qu'un segment d'UNE case. Au-delà, une suite de
        // cases franchissables qui court jusqu'au bord est bien plus souvent une
        // berge qu'un puits : on préfère la laisser sèche. Peindre de l'eau là où
        // il n'y en a pas tromperait le joueur ; l'oublier ne fait que le priver
        // d'un fond.
        const lClosed = (col === 0 && long === 1) || lWet || (col > 0 && isSolid((terrain[i - 1] ?? 0) as T))
        const rClosed = (fin === cols - 1 && long === 1) || rWet
          || (fin < cols - 1 && isSolid((terrain[row * cols + fin + 1] ?? 0) as T))
        let upUn = false, upTous = row > 0
        for (let c = col; c <= fin; c++) {
          const w = row > 0 && wet[(row - 1) * cols + c] === 1
          if (w) upUn = true; else upTous = false
        }
        // Un segment LONG doit en plus être couvert de nappe SUR TOUTE SA
        // LARGEUR. Sans cette condition, tout couloir horizontal sec reliant
        // deux colonnes d'eau se remplissait — mesuré avant/après sur les 28
        // vraies cartes : 50 cases peintes en eau à tort, dont un couloir entier
        // de « Phare à on! » et trois de « Gorges wecoulcowl ». Une nappe
        // descend par le haut, sur toute sa largeur : une corde posée dans un
        // bassin a de l'eau au-dessus d'un bout à l'autre, une anse sèche au
        // bord du bassin n'en a que d'un côté. C'est ce qui les sépare.
        if (lClosed && rClosed && (long === 1 ? (upUn || lWet || rWet) : upTous)) {
          for (let c = col; c <= fin; c++) wet[row * cols + c] = 1
          changed = true
        }
        col = fin + 1
      }
    }
  }
  return wet
}
